import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { userApi } from '@/api/user'
import { smsApi, APP_LOGIN_SMS_CODE, type SendSmsCodeData } from '@/api/sms'
import { ElMessage } from 'element-plus'
import { useLibraryCacheStore } from '@/stores/libraryCache'
import { useWatermarkStore } from '@/stores/watermark'
import { useWalletStore } from '@/stores/wallet'
import { useUserProfileStore } from '@/stores/userProfile'
import { useMembershipStore } from '@/stores/membership'
import { useMessageStore } from '@/stores/message'
import { useMessageDrawerStore } from '@/stores/messageDrawer'

import type { User } from '@/types'

const getV1ErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object') {
    const err = error as { response?: { data?: { message?: string } }; message?: string }
    const message = err.response?.data?.message || err.message
    if (typeof message === 'string' && message.trim() && message !== 'Not Authenticated') {
      return message
    }
  }
  return fallback
}

/**
 * 用户信息接口（基于后端返回的数据结构）
 */

export const useUserStore = defineStore('user', () => {
  // 状态
  const userInfo = ref<User | null>(null)

  // 防止重复请求的标志
  const isGettingUserInfo = ref(false)
  const isLoggedIn = computed(() => !!userInfo.value?.token)

  // 初始化用户信息
  const initUserInfo = () => {
    const storedUserInfo = localStorage.getItem('userInfo')
    const token = localStorage.getItem('token')

    if (storedUserInfo && token) {
      try {
        const parsed = JSON.parse(storedUserInfo)
        const refreshToken = parsed.refreshToken || localStorage.getItem('refreshToken') || ''
        userInfo.value = {
          ...parsed,
          token,
          refreshToken: refreshToken || parsed.refreshToken,
        }
      } catch (error) {
        console.error('❌ 解析用户信息失败:', error)
        clearUserInfo()
      }
    } else {
      console.log('⚠️ 缺少 storedUserInfo 或 token，无法初始化')
    }
  }

  // 登录
  const login = async (phone: string, verificationCode: string, invitationsCode?: string) => {
    // 登录前先清空旧的用户数据，确保数据干净
    clearUserInfo()

    try {
      const response = await userApi.mobileLogin({
        mobile: phone,
        code: verificationCode,
        smsPushCode: APP_LOGIN_SMS_CODE,
        invitationCode: invitationsCode,
      })

      if (response.code === 0 && response.data?.accessToken) {
        const loginData = response.data
        const profile = loginData.user
        const newUserInfo: User = {
          phone,
          token: loginData.accessToken,
          refreshToken: loginData.refreshToken,
          tokenType: loginData.tokenType,
          expiresIn: loginData.expiresIn,
          userId: profile?.userId != null ? String(profile.userId) : undefined,
          uuid: profile?.uuid,
          mobile: profile?.mobile || phone,
          nickname: profile?.nickname,
          invitationCode: profile?.invitationCode,
        }

        applyTokens(loginData.accessToken, loginData.refreshToken, newUserInfo)
        void useWalletStore().fetchAccounts()
        void useUserProfileStore().fetchDetail()
        void useMembershipStore().fetchMemberships()
        void useMessageStore().fetchCategories()

        ElMessage.success(response.message || '登录成功')
        return true
      } else {
        ElMessage.error(response.message || '登录失败')
        return false
      }
    } catch (error) {
      console.error('登录失败:', error)
      ElMessage.error(getV1ErrorMessage(error, '登录失败，请检查手机号和验证码'))
      return false
    }
  }

  // 退出登录：请求 POST /v1/users/logout 撤销当前会话，并清除本地令牌与用户缓存
  const logout = async () => {
    try {
      if (userInfo.value?.token) {
        await userApi.logout()
      }
    } catch (error) {
      console.error('退出登录接口调用失败:', error)
      // 即使接口调用失败（含会话已撤销的 401），也要清除本地用户信息
    } finally {
      clearUserInfo()
      ElMessage.success('已退出登录')
    }
  }

  // 清除用户信息
  const clearUserInfo = () => {
    // 清除响应式状态
    userInfo.value = null

    // 清除本地存储 - 用户核心数据
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('userInfo')

    // 清除其他可能的用户相关数据
    localStorage.removeItem('userPreferences')
    localStorage.removeItem('userSettings')
    localStorage.removeItem('userData')
    localStorage.removeItem('user')

    // 清除可能的缓存数据
    localStorage.removeItem('userCache')
    localStorage.removeItem('authCache')

    // 清除无水印下载相关设置
    // 同步重置 Pinia 内存态，避免页面 KeepAlive/组件仍持有旧状态
    try {
      const watermarkStore = useWatermarkStore()
      watermarkStore.resetRemoveWatermark()
      watermarkStore.resetRules?.()
    } catch (error) {
      console.warn('重置水印相关状态失败（忽略）:', error)
    }

    localStorage.removeItem('remove_watermark_enabled')
    localStorage.removeItem('watermark_disclaimer_no_remind')

    // 清除库缓存（模特场景库、商品场景库等）
    try {
      const libraryCacheStore = useLibraryCacheStore()
      libraryCacheStore.clearAll()
      // 同时清除 localStorage 中的库缓存数据
      localStorage.removeItem('libraryCache:v1')
    } catch (error) {
      console.warn('清除库缓存失败:', error)
    }

    try {
      const walletStore = useWalletStore()
      walletStore.reset()
    } catch (error) {
      console.warn('重置钱包状态失败（忽略）:', error)
    }

    try {
      const userProfileStore = useUserProfileStore()
      userProfileStore.reset()
    } catch (error) {
      console.warn('重置用户信息状态失败（忽略）:', error)
    }

    try {
      const membershipStore = useMembershipStore()
      membershipStore.reset()
    } catch (error) {
      console.warn('重置会员套餐状态失败（忽略）:', error)
    }

    try {
      const messageStore = useMessageStore()
      messageStore.reset()
    } catch (error) {
      console.warn('重置消息提醒状态失败（忽略）:', error)
    }

    try {
      const messageDrawerStore = useMessageDrawerStore()
      messageDrawerStore.reset()
    } catch (error) {
      console.warn('重置消息抽屉状态失败（忽略）:', error)
    }

    // 清除 sessionStorage 中的所有用户数据（包括个人资产列表滚动位置等）
    sessionStorage.clear()

    console.log('✅ 用户信息及所有缓存数据已完全清除')
  }

  const applyTokens = (accessToken: string, refreshToken?: string, nextUserInfo?: User) => {
    localStorage.setItem('token', accessToken)
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken)
    }

    const merged: User | null = nextUserInfo
      ? nextUserInfo
      : userInfo.value
        ? {
            ...userInfo.value,
            token: accessToken,
            refreshToken: refreshToken || userInfo.value.refreshToken,
          }
        : null

    if (merged) {
      userInfo.value = merged
      console.log('applyTokens...')
      console.log(merged)
      localStorage.setItem('userInfo', JSON.stringify(merged))
    }
  }

  /** 将 isVip 合并进当前 userInfo 并回写 localStorage，基于最新内存态，避免覆盖并发刷新的 token */
  const applyIsVip = (isVip: number) => {
    if (!userInfo.value) {
      return
    }

    const merged: User = {
      ...userInfo.value,
      isVip,
    }
    userInfo.value = merged
    localStorage.setItem('userInfo', JSON.stringify(merged))
  }

  // 获取验证码
  const getSmsCode = async (mobile: string): Promise<SendSmsCodeData | true | null> => {
    try {
      const response = await smsApi.sendVerificationCode({
        mobile,
        smsPushCode: APP_LOGIN_SMS_CODE,
      })

      if (response.code === 0) {
        ElMessage.success(response.message || '验证码已发送')
        return response.data || true
      } else {
        ElMessage.error(response.message || '获取验证码失败')
        return null
      }
    } catch (error) {
      console.error('获取验证码失败:', error)
      ElMessage.error(getV1ErrorMessage(error, '获取验证码失败，请稍后重试'))
      return null
    }
  }

  // 获取用户信息
  const getUserInfo = async (mobile: string) => {
    // 检查登录状态
    if (!isLoggedIn.value) return null

    // 防止重复请求
    if (isGettingUserInfo.value) {
      console.log('正在获取用户信息，跳过重复请求')
      return userInfo.value
    }

    isGettingUserInfo.value = true

    try {
      const response = await userApi.getUserInfo(mobile)

      if (response.resp_code === 0) {
        // 更新用户信息
        if (userInfo.value) {
          userInfo.value = {
            ...userInfo.value,
            ...response.datas,
          }
          // 更新本地存储
          localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
        }
        return response.datas
      } else {
        ElMessage.error(response.resp_msg || '获取用户信息失败')
        return null
      }
    } catch (error) {
      console.error('获取用户信息失败:', error)
      ElMessage.error('获取用户信息失败，请稍后重试')
      return null
    } finally {
      // 重置请求标志
      isGettingUserInfo.value = false
    }
  }

  // 初始化
  initUserInfo()

  return {
    userInfo,
    isLoggedIn,
    isGettingUserInfo,
    login,
    logout,
    clearUserInfo,
    applyTokens,
    applyIsVip,
    getSmsCode,
    getUserInfo,
  }
})
